import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonTitle,
  IonToolbar,
  IonAlert,
  IonText,
  IonGrid,
  IonRow,
  IonCol
} from '@ionic/react';
import {
  close,
  pencil,
  checkmark,
  arrowBack
} from 'ionicons/icons';
import permissionService from '@/services/PermissionService';
import ModalFooter from '@/components/common/ModalFooter';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { validateGroupForm } from '@/services/util/planningUtils';

export type GroupModalMode = 'add' | 'view' | 'edit';

export interface GroupData {
  id?: number;
  identifier: string;
  title: string;
}

interface GroupModalProps {
  isOpen: boolean;
  mode: GroupModalMode;
  groupData?: GroupData | null;
  existingGroups: GroupData[];
  onDidDismiss: () => void;
  onSave: (groupData: GroupData, mode: GroupModalMode) => Promise<void>;
}

const GroupModal: React.FC<GroupModalProps> = ({
  isOpen,
  mode: initialMode,
  groupData,
  existingGroups: _existingGroups,
  onDidDismiss,
  onSave
}) => {
  const [mode, setMode] = useState<GroupModalMode>(initialMode);
  const [formData, setFormData] = useState<GroupData>({
    identifier: '',
    title: ''
  });
  const [originalFormData, setOriginalFormData] = useState<GroupData>({
    identifier: '',
    title: ''
  });
  const [loading, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'close' | 'cancel' | null>(null);
  const [errors, setErrors] = useState<{
    identifier?: string;
    title?: string;
  }>({});

  useEffect(() => {
    setMode(initialMode);
    if (groupData) {
      const data = {
        id: groupData.id,
        identifier: groupData.identifier,
        title: groupData.title
      };
      setFormData(data);
      setOriginalFormData(data);
    } else {
      const emptyData = { identifier: '', title: '' };
      setFormData(emptyData);
      setOriginalFormData(emptyData);
    }
    setErrors({});
  }, [isOpen, initialMode, groupData]);

  const hasChanges = () => {
    return formData.identifier !== originalFormData.identifier ||
           formData.title !== originalFormData.title;
  };

  const validateForm = (): boolean => {
    const newErrors = validateGroupForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setToastMessage('Please correct the errors before saving');
      setToastVariant('error');
      setShowToast(true);
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...formData,
        identifier: formData.identifier.trim(),
        title: formData.title.trim(),
      }, mode);
      setToastMessage(mode === 'add' ? 'Group created successfully' : 'Group updated successfully');

      setToastVariant('success');
      setShowToast(true);

      // Update original data to reflect saved state
      setOriginalFormData({ ...formData });

      // Auto-close modal after successful save
      setTimeout(() => {
        onDidDismiss();
      }, 1000);

    } catch (error) {
      console.error('Error saving group:', error);
      setToastMessage('Failed to save group');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleCancel = () => {
    if (hasChanges()) {
      setConfirmAction('cancel');
      setShowConfirmAlert(true);
    } else {
      if (mode === 'edit') {
        setMode('view');
      } else if (mode === 'add') {
        onDidDismiss();
      }
    }
  };

  const handleClose = () => {
    if (hasChanges()) {
      setConfirmAction('close');
      setShowConfirmAlert(true);
    } else {
      onDidDismiss();
    }
  };

  const handleConfirmAction = () => {
    if (confirmAction === 'close') {
      onDidDismiss();
    } else if (confirmAction === 'cancel') {
      // Reset form data
      setFormData({ ...originalFormData });
      setErrors({});
      if (mode === 'edit') {
        setMode('view');
      } else if (mode === 'add') {
        onDidDismiss();
      }
    }
    setShowConfirmAlert(false);
    setConfirmAction(null);
  };

  const isReadOnly = mode === 'view';
  const isEditing = mode === 'edit' || mode === 'add';

  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{ mode === 'add' ? 'Add New Group' : formData.identifier + ' ' + formData.title }</IonTitle>
            <IonButtons slot="end">
              {mode === 'view' && permissionService.canEditGroup() && (
                <IonButton onClick={handleEdit} fill="clear">
                  <IonIcon icon={pencil} />
                </IonButton>
              )}
              <IonButton onClick={handleClose} fill="clear">
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <IonGrid>
              <IonRow>
                <IonCol size="12">
                  <IonItem>
                    <IonLabel position="stacked" style={{ marginBottom: '5px' }}>
                      Group Identifier <span style={{ color: 'var(--ion-color-danger)' }}>*</span>
                    </IonLabel>
                    <IonInput
                      value={formData.identifier}
                      placeholder="Enter group number"
                      readonly={isReadOnly}
                      onIonInput={(e) => setFormData({ ...formData, identifier: e.detail.value! })}
                      style={{
                        '--background': isReadOnly ? 'var(--ion-color-light)' : 'white'
                      }}
                    />
                  </IonItem>
                  {errors.identifier && (
                    <IonText color="danger" style={{ fontSize: '0.8rem', marginLeft: '16px' }}>
                      {errors.identifier}
                    </IonText>
                  )}
                </IonCol>
              </IonRow>

              <IonRow>
                <IonCol size="12">
                  <IonItem>
                    <IonLabel position="stacked" style={{ marginBottom: '5px' }}>
                      Title <span style={{ color: 'var(--ion-color-danger)' }}>*</span>
                    </IonLabel>
                    <IonInput
                      value={formData.title}
                      placeholder="Enter group title"
                      readonly={isReadOnly}
                      onIonInput={(e) => setFormData({ ...formData, title: e.detail.value! })}
                      style={{
                        '--background': isReadOnly ? 'var(--ion-color-light)' : 'white'
                      }}
                    />
                  </IonItem>
                  {errors.title && (
                    <IonText color="danger" style={{ fontSize: '0.8rem', marginLeft: '16px' }}>
                      {errors.title}
                    </IonText>
                  )}
                </IonCol>
              </IonRow>
            </IonGrid>
          </div>
        </IonContent>

        {isEditing && (
          <ModalFooter
            cancelLabel="Cancel"
            actionLabel={loading ? 'Saving...' : 'Save'}
            cancelIcon={arrowBack}
            actionIcon={checkmark}
            onCancel={handleCancel}
            onAction={handleSave}
            isCancelDisabled={loading}
            isActionDisabled={loading}
            isActionLoading={loading}
          />
        )}
      </IonModal>

      <Toaster
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      <IonAlert
        isOpen={showConfirmAlert}
        onDidDismiss={() => setShowConfirmAlert(false)}
        header="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to continue? Your changes will be lost."
        buttons={[
          {
            text: 'Stay',
            role: 'cancel',
            cssClass: 'secondary'
          },
          {
            text: 'Discard Changes',
            handler: handleConfirmAction
          }
        ]}
      />
    </>
  );
};

export default GroupModal;